import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

export const SUCCESS_CLOSE_MS = 10000; //The wait time for screen to retunrn to tv

type Props = {
  open: boolean;
  durationMs?: number;
  onElapsed: () => void;
};

/**
 * Success-screen close countdown. The session stays live until this elapses —
 * the parent is responsible for hanging up in `onElapsed`.
 */
export function SuccessCloseTimer({
  open,
  durationMs = SUCCESS_CLOSE_MS,
  onElapsed,
}: Props) {
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;

  const [remainingMs, setRemainingMs] = useState(durationMs);

  useEffect(() => {
    if (!open) {
      setRemainingMs(durationMs);
      return;
    }

    const started = performance.now();
    let raf = 0;
    let fired = false;

    const tick = (now: number) => {
      const left = Math.max(0, durationMs - (now - started));
      setRemainingMs(left);
      if (left <= 0) {
        if (!fired) {
          fired = true;
          onElapsedRef.current();
        }
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, durationMs]);

  if (!open) return null;

  const secondsLeft = Math.max(1, Math.ceil(remainingMs / 1000));
  const progress = remainingMs / durationMs;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "16px",
        px: "24px",
        pb: "24px",
      }}
    >
      <Typography
        aria-live="polite"
        sx={{
          fontFamily: "var(--font-sans)",
          fontWeight: 500,
          fontSize: "22px",
          lineHeight: "32px",
          textAlign: "center",
          color: "hsla(0, 0%, 100%, 0.88)",
        }}
      >
        This window will close in{" "}
        <Box component="span" sx={{ color: "#16c784", fontWeight: 700 }}>
          {secondsLeft}
        </Box>{" "}
        {secondsLeft === 1 ? "second" : "seconds"}
      </Typography>

      <Box
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={durationMs}
        aria-valuenow={Math.round(remainingMs)}
        aria-label="Time until this window closes"
        sx={{
          width: "100%",
          height: "8px",
          borderRadius: "100px",
          bgcolor: "rgba(22, 199, 132, 0.18)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${progress * 100}%`,
            height: "100%",
            borderRadius: "inherit",
            bgcolor: "#16c784",
          }}
        />
      </Box>
    </Box>
  );
}
